import { createAction, createSlice, current } from '@reduxjs/toolkit';
import {
    runDocumentFiltersMutation,
    runDocumentFiltersQuery,
} from "@/common/document-filters/document-filters.query";
import { notifyUnlessUnauthorized } from "@/pages/shared/rtk-query-helpers";
import { openConfirmModal } from "@/notification";
import { config } from '@/config';
import { v4 as uuidv4 } from 'uuid';
import get from 'lodash.get';
//import { setSiteValues } from "@/pages/dashboard/dashboard.slice"

const SET_DOCUMENT_FILTER_VALUE = 'SET_DOCUMENT_FILTER_VALUE'
const SET_CATEGORY = 'SET_CATEGORY'
const SET_SUB_CATEGORY = 'SET_SUB_CATEGORY'
const SET_SELECTED_VALUE = 'SET_SELECTED_VALUE'
const CLEAR_FILTER = 'CLEAR_FILTER'
const CLEAR_CUSTOM_FILTER = 'CLEAR_CUSTOM_FILTER';
const CLEAR_DETAILS_FILTER = 'CLEAR_DETAILS_FILTER';
const FILTER_UPDATE = 'DOCUMENTS_FILTER_UPDATE'

export const DOCUMENT_FILTERS_SUCCESS = 'DOCUMENT_FILTERS_SUCCESS';

const initialState = {
    key: uuidv4(),
    categoryList: {
        loading: false,
        items: {}
    },
    filterDrawerOpen: false,
    subCategoryList: {
        loading: false,
        items: {}
    },
    customDocumentFilters: null,
    documentStatusesList: [
        { id: 1, name: "Current" },
        { id: 2, name: "Draft" },
        { id: 3, name: "Current and Retired" },
        { id: 4, name: "Marked for Change" },
        { id: 6, name: "Reserved" },
        { id: 5, name: "Retired" }
    ],
    checkersList: {
        items: [],
        loading: false
    },
    selected: {
        documentStatusId: 1,
        category: '',
        subCategory: '',
        showAllRevision: false,
        serverMessage: '',
        documentNumber: '',
        vendorNumber: [],
        keywords: [],
        loadingAllFilters: false
    }
}

// Legacy action type strings are preserved so existing dispatch sites keep working.
export const setValue = createAction(SET_DOCUMENT_FILTER_VALUE, (inputName, value) => ({
    payload: { name: inputName, value }
}));

export const setCategory = createAction(SET_CATEGORY, (element, value) => ({
    payload: { element, value }
}));

export const setSubCategory = createAction(SET_SUB_CATEGORY, (element, value) => ({
    payload: { element, value }
}));

export const setSelectedValue = createAction(SET_SELECTED_VALUE, (inputName, value) => ({
    payload: { inputName, value }
}));

export const clearSelectedFilter = createAction(CLEAR_FILTER);

export const clearCustomFilter = createAction(CLEAR_CUSTOM_FILTER);

export const clearDetailsFilter = createAction(CLEAR_DETAILS_FILTER);

export const documentFiltersUpdate = createAction(FILTER_UPDATE);

export const documentFiltersSuccess = createAction(
    DOCUMENT_FILTERS_SUCCESS,
    (value, tableId, updateCustomFilters) => ({
        payload: { value, tableId, updateCustomFilters }
    })
);

// Replaces immutability-helper `update.set`: assigns a dotted path, creating missing objects.
const setPathValue = (draft, path, value) => {
    const segments = String(path).split('.');
    let target = draft;

    for (let index = 0; index < segments.length - 1; index += 1) {
        const segment = segments[index];

        if (target[segment] === null || typeof target[segment] !== 'object') {
            target[segment] = {};
        }

        target = target[segment];
    }

    target[segments[segments.length - 1]] = value;
};

const documentFiltersSlice = createSlice({
    name: 'documentFilters',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(setValue, (draft, action) => {
                setPathValue(draft, action.payload.name, action.payload.value);
            })
            .addCase(setCategory, (draft, action) => {
                draft.categoryList[action.payload.element] = action.payload.value;
            })
            .addCase(setSubCategory, (draft, action) => {
                draft.subCategoryList[action.payload.element] = action.payload.value;
            })
            .addCase(clearCustomFilter, (draft) => {
                Object.keys(draft.selected)
                    .filter(item => item.includes('filter'))
                    .forEach(item => {
                        draft.selected[item] = "";
                    });
            })
            .addCase(clearDetailsFilter, (draft) => {
                Object.keys(draft.selected)
                    .filter(item => !item.includes('filter') && item != 'documentStatusId')
                    .forEach(item => {
                        draft.selected[item] = "";
                    });

                draft.subCategoryList = { ...initialState.subCategoryList };
            })
            .addCase(documentFiltersUpdate, (draft) => {
                draft.key = uuidv4();
            })
            .addCase(documentFiltersSuccess, (draft, action) => {
                const { value, tableId, updateCustomFilters } = action.payload;

                if (value.allCategories) {
                    setPathValue(draft, `${tableId}.allCategories`, value.allCategories);
                    draft.allCategories = value.allCategories;
                    draft.siteCategories = value.allCategories;
                }

                if (value.allSubCategories) {
                    setPathValue(draft, `${tableId}.allSubCategories`, value.allSubCategories);
                    draft.allSubCategories = value.allSubCategories;
                    draft.siteSubCategories = value.allSubCategories;
                }

                setPathValue(draft, `${tableId}.categories`, value.categories);
                setPathValue(draft, `${tableId}.subCategories`, value.subCategories);

                if (updateCustomFilters) {
                    setPathValue(draft, `${tableId}.customDocumentFilters`, value.documentFilters);
                    return;
                }

                const customFilters = get(current(draft), `${tableId}.customDocumentFilters`);

                if (!customFilters) {
                    return;
                }

                const filters = customFilters.map((x) => {
                    const filter = value.documentFilters.find(
                        (y) => y.filterName.toLowerCase() === x.filterName.toLowerCase()
                    );
                    return {
                        ...x,
                        values: filter ? filter.values : [],
                    };
                });

                setPathValue(draft, `${tableId}.customDocumentFilters`, filters);
            });
    }
});

export const documentFiltersReducer = documentFiltersSlice.reducer;


export const loadListOfCategories = (siteId, Category, documentStatusId, filterOptions = {}) => {
    return async (dispatch) => {
        dispatch(setCategory('loading', true));

        try {
            const data = await runDocumentFiltersQuery(dispatch, 'getCategoryList', {
                siteId,
                subCategory: Category,
                documentStatusId,
                filterOptions,
            });

            setTimeout(() => {
                dispatch(setCategory('items', data));
            }, config.LOADING_DELAY);
        } catch (error) {
            notifyUnlessUnauthorized(error, `Can't load list of categories. Please reload page`);
        } finally {
            setTimeout(() => {
                dispatch(setCategory('loading', false));
            }, config.LOADING_DELAY);
        }
    }
}

export const loadAllCategories = (siteId, documentStatusId) => {
    return async (dispatch) => {
        dispatch(setCategory('loading', true));

        try {
            const data = await runDocumentFiltersQuery(dispatch, 'getCategoryList', {
                siteId,
                subCategory: null,
                documentStatusId,
            });

            setTimeout(() => {
                dispatch(setValue('allCategories', data));
                dispatch(setValue('siteCategories', data));
            }, config.LOADING_DELAY);
        } catch (error) {
            notifyUnlessUnauthorized(error, `Can't load list of categories. Please reload page`);
        } finally {
            setTimeout(() => {
                dispatch(setCategory('loading', false));
            }, config.LOADING_DELAY);
        }
    }
}

export const loadAllCartCategories = (siteId, documentStatusId) => {
    return async (dispatch) => {
        dispatch(setCategory('loading', true));

        try {
            const data = await runDocumentFiltersQuery(dispatch, 'getCategoryList', {
                siteId,
                subCategory: null,
                documentStatusId,
            });

            setTimeout(() => {
                dispatch(setValue('allCartCategories', data));
            }, config.LOADING_DELAY);
        } catch (error) {
            notifyUnlessUnauthorized(error, `Can't load list of categories. Please reload page`);
        } finally {
            setTimeout(() => {
                dispatch(setCategory('loading', false));
            }, config.LOADING_DELAY);
        }
    }
}

export const loadAllSubCategories = (siteId, documentStatusId) => {
    return async (dispatch) => {
        dispatch(setSubCategory('loading', true));

        try {
            const data = await runDocumentFiltersQuery(dispatch, 'getSubCategoryList', {
                siteId,
                category: null,
                documentStatusId,
            });

            setTimeout(() => {
                dispatch(setValue('allSubCategories', data));
                dispatch(setValue('siteSubCategories', data));
            }, config.LOADING_DELAY);
        } catch (error) {
            notifyUnlessUnauthorized(error, "Can't load list of sub categories. Please reload page");
        } finally {
            setTimeout(() => {
                dispatch(setSubCategory('loading', false));
            }, config.LOADING_DELAY);
        }
    }
}

export const loadAllCartSubCategories = (siteId, documentStatusId) => {
    return async (dispatch) => {
        dispatch(setSubCategory('loading', true));

        try {
            const data = await runDocumentFiltersQuery(dispatch, 'getSubCategoryList', {
                siteId,
                category: null,
                documentStatusId,
            });

            setTimeout(() => {
                dispatch(setValue('allCartSubCategories', data));
            }, config.LOADING_DELAY);
        } catch (error) {
            notifyUnlessUnauthorized(error, "Can't load list of sub categories. Please reload page");
        } finally {
            setTimeout(() => {
                dispatch(setSubCategory('loading', false));
            }, config.LOADING_DELAY);
        }
    }
}

export const loadListOfSubCategories = (siteId, subCategory, documentStatusId, filterOptions = {}) => {
    return async (dispatch) => {
        dispatch(setSubCategory('loading', true));

        try {
            const data = await runDocumentFiltersQuery(dispatch, 'getSubCategoryList', {
                siteId,
                category: subCategory,
                documentStatusId,
                filterOptions,
            });

            setTimeout(() => {
                dispatch(setSubCategory('items', data));
            }, config.LOADING_DELAY);
        } catch (error) {
            notifyUnlessUnauthorized(error, "Can't load list of sub categories. Please reload page");
        } finally {
            setTimeout(() => {
                dispatch(setSubCategory('loading', false));
            }, config.LOADING_DELAY);
        }
    }
}

export const getDocumentFilters = (siteId, showConfirmModal) => {
    return async (dispatch) => {
        try {
            const data = await runDocumentFiltersQuery(dispatch, 'getDocumentFilters', siteId);

            dispatch(setCustomDocumentFilters(data));

            if ((!data || !data.length) && showConfirmModal) {
                openConfirmModal("The Upload Documents Tool requires that each uploaded document have at least one filter populated. The site you have selected has no filters turned on to allow a successful upload. Filters are populated by Managers and above at the following location: Management/Account/Site/Filters.");
            }
        } catch (error) {
            notifyUnlessUnauthorized(error, "Can't load document filters. Please reload page");
        }
    }
}

export const loadCheckers = (siteId) => {
    return (dispatch) => {
        dispatch(setValue('checkersList.loading', true));

        runDocumentFiltersQuery(dispatch, 'getCheckers', siteId)
            .then(data => {
                setTimeout(() => {
                    dispatch(setValue('checkersList.items', data));
                }, config.LOADING_DELAY);
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, "Can't load checkers. Please reload page");
            });

        dispatch(setValue('checkersList.loading', false));
    }
}

export const loadAllCustomFilters = (model, table) => {
    return async (dispatch) => {
        dispatch(setValue('loadingAllFilters', true));

        try {
            const data = await runDocumentFiltersMutation(dispatch, 'getDashboardFilters', model);

            if (table == "cartManagementTable") {
                dispatch(setValue('allCartCustomDocumentFilters', data.documentFilters));
            } else {
                dispatch(setValue('allCustomDocumentFilters', data.documentFilters));
            }
        } catch {
            // Intentionally silent: matches the previously commented-out error branch.
        } finally {
            dispatch(setValue('loadingAllFilters', false));
        }
    }
}

export const getDashboardFilters = (filters, tableId, updateCustomerFilters) => {
    return async (dispatch) => {
        dispatch(setValue('loadingAllFilters', true));

        try {
            const data = await runDocumentFiltersMutation(dispatch, 'getDashboardFilters', filters);

            dispatch(documentFiltersSuccess(data, tableId, updateCustomerFilters));
        } catch (error) {
            notifyUnlessUnauthorized(error, `Error. Can't load filters`);
        } finally {
            setTimeout(() => {
                dispatch(setValue('loadingAllFilters', false));
            }, 100)
        }
    }
}

export const setCustomDocumentFilters = (data) => {
    return (dispatch) => {
        dispatch(setValue('customDocumentFilters', data));
    }
}